import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Modal,
  Pressable,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useInitiativesQuery } from '../../hooks/queries';
import { OrganizationFilterDropdown, useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Circle, G, Path } from 'react-native-svg';
import { PlusIcon, ExportIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, EllipsisVerticalIcon, EditIcon, TrashIcon, XIcon } from '../../components/Icons';
import { initiativesApi, goalsApi, kpisApi } from '../../services/apiService';
import { downloadBlobAsFile } from '../../utils/downloadFile';
import { commitmentColor } from '../../utils/metricColors';
import { InitiativeList, StrategicGoalList } from '../../types';
import { COLORS, FONTS, LABELS } from '../../constants/config';
import { InitiativesSkeleton, EmptyState, PageContainer, Navbar, SKELETON_DIMENSIONS, ViewOnlyBadge, Breadcrumb, EvidenceTable } from '../../components';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'Initiatives'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// Import (file-input) icon — matches the unified import button across list screens
const FileInputIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Circular Gauge Component
// ============================================================

interface CircularGaugeProps {
  percentage: number | null | undefined;
  size?: number;
  strokeWidth?: number;
  textColor?: string;
  trackColor?: string;
}

const CircularGauge = ({ percentage, size = 80, strokeWidth = 6, textColor = tokens.colors.semantic.general.foreground, trackColor = tokens.colors.raw.gray["200"] }: CircularGaugeProps) => {
  // Commitment rate is null when no measurement period is due yet → show "--"
  if (percentage == null) {
    return (
      <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ fontSize: tokens.typography.paragraph["paragraph font size"], fontFamily: FONTS.numbers, fontWeight: '500', color: tokens.colors.raw.gray["400"] }}>--</Text>
      </View>
    );
  }
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Unified commitment color scale (>=80 green / >=50 orange / <50 red)
  const getColor = () => commitmentColor(percentage);

  // Calculate marker dot position (at the end of the progress arc)
  const progressAngle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const markerX = center + radius * Math.cos(progressAngle);
  const markerY = center + radius * Math.sin(progressAngle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={getColor()}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Marker dot at progress end */}
        <G>
          <Circle
            cx={markerX}
            cy={markerY}
            r={6}
            fill={getColor()}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        </G>
      </Svg>
      {/* Percentage text */}
      <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ fontSize: tokens.typography.paragraph["paragraph font size"], fontFamily: FONTS.numbers, fontWeight: '500', color: textColor }}>
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Initiative Card Component
// ============================================================

interface ThemedCardStyles {
  card: object;
  cardTitle: object;
  cardValue: object;
  cardDescription: object;
  sectionTitle: object;
  pageSubtitle: object;
}

interface InitiativeCardProps {
  initiative: InitiativeList;
  onPress: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isViewOnly?: boolean;
  themedCardStyles?: ThemedCardStyles;
  iconColor?: string;
  textColor?: string;
  trackColor?: string;
  dropdownBg?: string;
  dropdownBorder?: string;
  statBoxBg?: string;
  t: (key: string) => string;
  translate: (text: string | undefined | null) => string;
  translateOrg: (name: string | undefined | null) => string;
}

const InitiativeCard = ({ initiative, onPress, onEdit, onDelete, isViewOnly, themedCardStyles, iconColor = tokens.colors.semantic.general.foreground, textColor = tokens.colors.semantic.general.foreground, trackColor = tokens.colors.raw.gray["200"], dropdownBg = tokens.colors.raw.white, dropdownBorder = tokens.colors.raw.slate["200"], statBoxBg = tokens.colors.raw.slate["100"], t, translate, translateOrg }: InitiativeCardProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const isPending = initiative.approval_status === 'pending';
  // Commitment rate for the card donut; null (nothing due yet) → "--"
  const percentage = initiative.commitment_rate ?? null;
  const organizationName = translateOrg(initiative.department_name) || t('common.notSpecified');

  const handleMenuPress = () => {
    setShowMenu(!showMenu);
  };

  const handleEdit = () => {
    setShowMenu(false);
    onEdit();
  };

  const handleDelete = () => {
    setShowMenu(false);
    onDelete();
  };

  return (
    <View style={[styles.initiativeCard, { minHeight: SKELETON_DIMENSIONS.INITIATIVE_CARD_HEIGHT, flex: 1 }, themedCardStyles?.card]}>
      {/* Top Section: Different layout for pending vs approved */}
      <View style={styles.cardTopSection}>
        {isPending ? (
          // Pending State: Purple badge on left, title section on right
          <>
            {/* Left: Status Badge (Purple) */}
            <View style={styles.pendingBadge}>
              <Text style={styles.pendingBadgeText}>{t('initiatives.pendingApproval')}</Text>
            </View>
            {/* Right: Title and Org Badge */}
            <View style={styles.cardTitleSection}>
              <Text style={[styles.cardTitle, themedCardStyles?.cardTitle]} numberOfLines={2}>
                {translate(initiative.title)}
              </Text>
              <View style={styles.orgBadge}>
                <Text style={styles.orgBadgeText}>{organizationName}</Text>
              </View>
            </View>
          </>
        ) : (
          // Approved/In Progress State: Gauge on left, title section on right
          <>
            {/* Left: Circular Gauge */}
            <View style={styles.gaugeContainer}>
              <CircularGauge percentage={percentage} size={80} strokeWidth={6} textColor={textColor} trackColor={trackColor} />
            </View>
            {/* Right: Title and Org Badge */}
            <View style={styles.cardTitleSection}>
              <Text style={[styles.cardTitle, themedCardStyles?.cardTitle]} numberOfLines={2}>
                {translate(initiative.title)}
              </Text>
              <View style={styles.orgBadge}>
                <Text style={styles.orgBadgeText}>{organizationName}</Text>
              </View>
            </View>
          </>
        )}
      </View>

      {/* Description */}
      <Text style={[styles.cardDescription, themedCardStyles?.cardDescription]} numberOfLines={2}>
        {translate(initiative.description) || '—'}
      </Text>

      {/* Pending Deletion Badge */}
      {initiative.has_pending_deletion && <PendingDeletionBadge compact />}

      {/* Spacer to push stats and footer to bottom */}
      <View style={{ flex: 1 }} />

      {/* Stats Row - Gray boxes */}
      <View style={styles.statsRow}>
        <View style={[styles.statBox, { backgroundColor: statBoxBg }]}>
          <Text style={[styles.statLabel, themedCardStyles?.cardDescription]}>{t('initiatives.kpiCount')}</Text>
          <Text style={[styles.statValue, themedCardStyles?.cardValue]}>{initiative.kpi_count ?? 0}</Text>
        </View>
        <View style={[styles.statBox, { backgroundColor: statBoxBg }]}>
          <Text style={[styles.statLabel, themedCardStyles?.cardDescription]}>{t('initiatives.activityCount')}</Text>
          <Text style={[styles.statValue, themedCardStyles?.cardValue]}>{initiative.activity_count ?? 0}</Text>
        </View>
      </View>

      {/* Footer: View Details (Left) + Menu (Right) */}
      <View style={styles.cardFooter}>
        <TouchableOpacity style={styles.viewDetailsButton} onPress={onPress}>
          <ChevronLeftIcon size={14} color={iconColor} />
          <Text style={[styles.viewDetailsText, { color: iconColor }]}>{t('common.viewDetails')}</Text>
        </TouchableOpacity>
        {/* Menu - hidden for view-only users (executive) */}
        {!isViewOnly && (
          <View style={styles.menuContainer}>
            <IconButton
              variant="ghost"
              size="sm"
              icon={<EllipsisVerticalIcon color={iconColor} />}
              accessibilityLabel={t('common.menu')}
              onPress={handleMenuPress}
            />
            {showMenu && (
              <>
                <Pressable style={styles.menuOverlay} onPress={() => setShowMenu(false)} />
                <View style={[styles.cardDropdownMenu, { backgroundColor: dropdownBg, borderColor: dropdownBorder }]}>
                  <TouchableOpacity style={[styles.cardDropdownItem, { borderBottomColor: dropdownBorder }]} onPress={handleEdit}>
                    <Text style={[styles.cardDropdownItemText, { color: iconColor }]}>{t('initiatives.editInitiative')}</Text>
                    <EditIcon size={16} color={iconColor} />
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.cardDropdownItemDelete} onPress={handleDelete}>
                    <Text style={styles.cardDropdownItemTextDelete}>{t('common.delete')}</Text>
                    <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

// ============================================================
// Pagination Component
// ============================================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange, nextLabel, previousLabel }: PaginationProps & { nextLabel: string; previousLabel: string }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <View style={[styles.paginationContainer, { flexDirection: rtlRow }]}>
      {/* Previous: السابق > */}
      <TouchableOpacity
        style={[styles.paginationNavButton, isFirstPage && styles.paginationNavButtonDisabled]}
        onPress={() => onPageChange(Math.max(currentPage - 1, 1))}
        disabled={isFirstPage}
      >
        <Text style={styles.paginationNavText}>{previousLabel}</Text>
        <ChevronRightIcon size={13} color={tokens.colors.raw.slate["700"]} />
      </TouchableOpacity>

      {/* Page Numbers */}
      <View style={[styles.paginationPages, { flexDirection: rtlRow }]}>
        {pages.map((page) => (
          <TouchableOpacity
            key={page}
            style={[
              styles.paginationPage,
              currentPage === page && styles.paginationPageActive,
            ]}
            onPress={() => onPageChange(page)}
          >
            <Text
              style={[
                styles.paginationPageText,
                currentPage === page && styles.paginationPageTextActive,
              ]}
            >
              {page}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Next: < التالي */}
      <TouchableOpacity
        style={[styles.paginationNavButton, isLastPage && styles.paginationNavButtonDisabled]}
        onPress={() => onPageChange(Math.min(currentPage + 1, totalPages))}
        disabled={isLastPage}
      >
        <ChevronLeftIcon size={13} color={tokens.colors.raw.slate["700"]} />
        <Text style={styles.paginationNavText}>{nextLabel}</Text>
      </TouchableOpacity>
    </View>
  );
};

// ============================================================
// Main Screen
// ============================================================

export default function InitiativesScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { goalId, organizationId } = route.params || {};
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme-aware styles for cards
  const themedCardStyles: ThemedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
    sectionTitle: { color: colors.textPrimary },
    pageSubtitle: { color: colors.textMuted },
  };

  // Theme colors for specific elements
  const themeColors = {
    statBox: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
    filterDropdown: colors.card,
    filterDropdownBorder: colors.cardBorder,
    filterDropdownText: colors.textPrimary,
    applyFilterBg: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"],
    applyFilterText: colors.textPrimary,
    exportButton: colors.card,
    exportButtonBorder: colors.cardBorder,
    exportButtonText: colors.textPrimary,
  };

  // Responsive breakpoints (for backward compatibility)
  const isSmallScreen = isMobile;

  // Check if user is org_employee (limited permissions)
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  // Check if user is strategy_team (they can't add initiatives)
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';

  // Evidence role flags
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = useCanViewAllOrgs();
  const ownDeptId = user?.profile?.organization;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 6;

  const queryClient = useQueryClient();

  // Delete confirmation modal state
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [initiativeToDelete, setInitiativeToDelete] = useState<InitiativeList | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filter state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0, width: 220 });
  const filterTriggerRef = useRef<View | null>(null);
  const [strategicGoals, setStrategicGoals] = useState<StrategicGoalList[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(goalId || null);
  const [tempSelectedGoalId, setTempSelectedGoalId] = useState<number | null>(goalId || null);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(organizationId ?? null);
  const effectiveOrgId = selectedOrgId ?? organizationId ?? undefined;

  // Use React Query for initiatives
  const {
    data: initiativesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInitiativesQuery({
    page: currentPage,
    pageSize: pageSize,
    goal: selectedGoalId || goalId,
    organization: effectiveOrgId,
    // Executive (view-only) users should only see approved initiatives
    approval_status: isViewOnly ? 'approved' : undefined,
    year: selectedYear,
  });

  const initiatives = initiativesData?.results || [];

  // Auto-refresh when screen regains focus (e.g. after add/edit)
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Update total pages when data changes
  useEffect(() => {
    if (initiativesData?.count) {
      setTotalPages(Math.ceil(initiativesData.count / pageSize));
    }
  }, [initiativesData?.count, pageSize]);

  // Fetch strategic goals for filter dropdown
  useEffect(() => {
    const fetchStrategicGoals = async () => {
      setLoadingGoals(true);
      try {
        const data = await goalsApi.list({ page_size: 100 });
        setStrategicGoals(data.results || []);
      } catch (err) {
        console.error('Error fetching strategic goals:', err);
      } finally {
        setLoadingGoals(false);
      }
    };
    fetchStrategicGoals();
  }, []);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGoalId, selectedOrgId]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const handleViewDetails = (id: number) => {
    navigation.navigate('InitiativeDetail', { id });
  };

  const handleEditInitiative = (initiative: InitiativeList) => {
    navigation.navigate('AddInitiative', { initiativeId: initiative.id });
  };

  const handleDeletePress = (initiative: InitiativeList) => {
    setInitiativeToDelete(initiative);
    setShowDeletionImpact(true);
  };

  const handleConfirmDelete = async () => {
    if (!initiativeToDelete) return;

    setDeleting(true);
    try {
      const response = await initiativesApi.delete(initiativeToDelete.id);

      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t('initiatives.deleteSuccess'));
      }

      // Invalidate the query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
    } catch (err: any) {
      if (err.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      } else if (err.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(err.response?.data?.detail || t('messages.error.deleteFailed'));
      }
    } finally {
      setDeleting(false);
      setShowDeletionImpact(false);
      setInitiativeToDelete(null);
    }
  };

  // Filter handlers
  const handleToggleFilterDropdown = () => {
    if (showFilterDropdown) {
      setShowFilterDropdown(false);
      return;
    }
    if (Platform.OS === 'web' && filterTriggerRef.current) {
      const node = filterTriggerRef.current as unknown as HTMLElement;
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        setFilterPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 220) });
      }
    } else if (filterTriggerRef.current && (filterTriggerRef.current as any).measureInWindow) {
      (filterTriggerRef.current as any).measureInWindow((x: number, y: number, w: number, h: number) => {
        setFilterPos({ top: y + h + 4, left: x, width: Math.max(w, 220) });
      });
    }
    setTempSelectedGoalId(selectedGoalId);
    setShowFilterDropdown(true);
  };

  const handleSelectGoal = (id: number | null) => {
    setTempSelectedGoalId(id);
    setSelectedGoalId(id); // Auto-apply filter on selection
    setShowFilterDropdown(false);
  };

  const handleApplyFilter = () => {
    setSelectedGoalId(tempSelectedGoalId);
    setShowFilterDropdown(false);
  };

  const handleClearFilter = () => {
    setSelectedGoalId(null);
    setTempSelectedGoalId(null);
  };

  // Get selected goal name for display
  const getSelectedGoalName = () => {
    if (!tempSelectedGoalId) return t('filters.allStrategicGoals');
    const goal = strategicGoals.find(g => g.id === tempSelectedGoalId);
    return translate(goal?.title) || t('filters.allStrategicGoals');
  };

  // Handle Excel export - fetch ALL data, not just current page
  const handleExportExcel = async () => {
    if (initiativesData?.count === 0) {
      showError(t('initiatives.noInitiativesToExport'));
      return;
    }

    setIsExporting(true);
    try {
      // Backend builds the report (initiatives + activity/initiative KPIs + grid), same filters.
      const params: Record<string, any> = { lang: language };
      if (selectedGoalId || goalId) params.goal = selectedGoalId || goalId;
      if (effectiveOrgId) params.organization = effectiveOrgId;
      if (isViewOnly) params.approval_status = 'approved';

      const response = await initiativesApi.exportExcel(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.initiatives')}.xlsx`);
      showSuccess(t('messages.success.exportedInitiatives', { count: initiativesData?.count ?? 0 }));
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showError(t('messages.error.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading && initiatives.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <InitiativesSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={LABELS.error}
        message={error.message}
        actionLabel={LABELS.refresh}
        onAction={refetch}
      />
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('navigation.strategicInitiatives') },
          ]}
        />

        <PageHeader
          title={t('navigation.strategicInitiatives')}
          actions={
            <View style={styles.headerButtons}>
              {!isStrategyTeam && !isViewOnly && (
                <Button
                  variant="primary"
                  onPress={() => navigation.navigate('AddInitiative', {})}
                  trailingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}
                >
                  {isMobile ? t('common.add') : t('initiatives.addInitiative')}
                </Button>
              )}
              {!isStrategyTeam && !isViewOnly && (
                <Button
                  variant="secondary"
                  onPress={() => navigation.navigate('ImportInitiatives' as any)}
                  trailingIcon={<FileInputIcon size={16} color={colors.textPrimary} />}
                >
                  {isMobile ? t('common.import') : t('importInitiatives.title')}
                </Button>
              )}
              <Button
                variant="secondary"
                onPress={handleExportExcel}
                trailingIcon={<ExportIcon size={16} color={colors.textSecondary} />}
              >
                {isMobile ? t('common.export') : t('common.exportExcel')}
              </Button>
            </View>
          }
          contentTrailing={
          <View style={[
            styles.filterRow,
            isMobile && { flexDirection: 'column-reverse', gap: tokens.spacing.absolute["2_5"], alignItems: 'stretch' }
          ]}>
            {/* Clear Filter Button - Only shown when filter is active (leftmost) */}
            {selectedGoalId && (
              <Button
                variant="destructive"
                onPress={handleClearFilter}
                leadingIcon={<XIcon size={14} color={tokens.colors.raw.white} />}
                style={{ flex: isMobile ? 1 : undefined }}
              >
                {t('filters.clearFilter')}
              </Button>
            )}

            {/* Apply Filter Button - Left */}
            <Button
              variant="secondary"
              onPress={handleApplyFilter}
              style={{ flex: isMobile ? 1 : undefined }}
            >
              {t('common.applyFilter')}
            </Button>

            {/* Dropdown - Right (shown last in row = right in RTL) */}
            <View style={[styles.filterDropdownContainer, { width: isMobile ? '100%' : 'auto' }]}>
              <View ref={filterTriggerRef as any} collapsable={false}>
                <TouchableOpacity
                  style={[
                    styles.filterDropdown,
                    {
                      minHeight: 44,
                      backgroundColor: themeColors.filterDropdown,
                      borderColor: themeColors.filterDropdownBorder,
                      width: '100%',
                    }
                  ]}
                  onPress={handleToggleFilterDropdown}
                >
                  <ChevronDownIcon size={16} color={colors.textSecondary} />
                  <Text
                    style={[styles.filterDropdownText, { color: themeColors.filterDropdownText }]}
                    numberOfLines={1}
                  >
                    {getSelectedGoalName()}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Dropdown Menu — portaled to body to escape stacking contexts */}
              {showFilterDropdown && Platform.OS === 'web' && createPortal(
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                    onClick={() => setShowFilterDropdown(false)}
                  />
                  <div
                    style={{
                      position: 'fixed',
                      top: filterPos.top,
                      insetInlineStart: filterPos.left,
                      width: filterPos.width,
                      zIndex: 9999,
                      backgroundColor: colors.card,
                      borderRadius: tokens.radii.absolute["radius-8"],
                      border: `1px solid ${colors.cardBorder}`,
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                      overflow: 'hidden',
                    }}
                  >
                    <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled>
                      <TouchableOpacity
                        style={[
                          styles.filterDropdownOption,
                          tempSelectedGoalId === null && styles.filterDropdownOptionSelected,
                          { borderBottomColor: colors.border }
                        ]}
                        onPress={() => handleSelectGoal(null)}
                      >
                        <Text style={[
                          styles.filterDropdownOptionText,
                          { color: colors.textPrimary },
                          tempSelectedGoalId === null && { fontFamily: FONTS.medium }
                        ]}>
                          {t('filters.allStrategicGoals')}
                        </Text>
                      </TouchableOpacity>

                      {loadingGoals ? (
                        <View style={styles.filterLoadingContainer}>
                          <ActivityIndicator size="small" color={colors.primary} />
                        </View>
                      ) : (
                        strategicGoals.map((goal) => (
                          <TouchableOpacity
                            key={goal.id}
                            style={[
                              styles.filterDropdownOption,
                              tempSelectedGoalId === goal.id && styles.filterDropdownOptionSelected,
                              { borderBottomColor: colors.border }
                            ]}
                            onPress={() => handleSelectGoal(goal.id)}
                          >
                            <Text
                              style={[
                                styles.filterDropdownOptionText,
                                { color: colors.textPrimary },
                                tempSelectedGoalId === goal.id && { fontFamily: FONTS.medium }
                              ]}
                              numberOfLines={2}
                            >
                              {translate(goal.title)}
                            </Text>
                          </TouchableOpacity>
                        ))
                      )}
                    </ScrollView>
                  </div>
                </>,
                document.body,
              )}

              {/* Native (non-web): fallback inline render */}
              {showFilterDropdown && Platform.OS !== 'web' && (
                <>
                  <Pressable style={styles.filterOverlay} onPress={() => setShowFilterDropdown(false)} />
                  <View style={[styles.filterDropdownMenu, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
                    <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled>
                      <TouchableOpacity
                        style={[
                          styles.filterDropdownOption,
                          tempSelectedGoalId === null && styles.filterDropdownOptionSelected,
                          { borderBottomColor: colors.border }
                        ]}
                        onPress={() => handleSelectGoal(null)}
                      >
                        <Text style={[
                          styles.filterDropdownOptionText,
                          { color: colors.textPrimary },
                          tempSelectedGoalId === null && { fontFamily: FONTS.medium }
                        ]}>
                          {t('filters.allStrategicGoals')}
                        </Text>
                      </TouchableOpacity>

                      {loadingGoals ? (
                        <View style={styles.filterLoadingContainer}>
                          <ActivityIndicator size="small" color={colors.primary} />
                        </View>
                      ) : (
                        strategicGoals.map((goal) => (
                          <TouchableOpacity
                            key={goal.id}
                            style={[
                              styles.filterDropdownOption,
                              tempSelectedGoalId === goal.id && styles.filterDropdownOptionSelected,
                              { borderBottomColor: colors.border }
                            ]}
                            onPress={() => handleSelectGoal(goal.id)}
                          >
                            <Text
                              style={[
                                styles.filterDropdownOptionText,
                                { color: colors.textPrimary },
                                tempSelectedGoalId === goal.id && { fontFamily: FONTS.medium }
                              ]}
                              numberOfLines={2}
                            >
                              {translate(goal.title)}
                            </Text>
                          </TouchableOpacity>
                        ))
                      )}
                    </ScrollView>
                  </View>
                </>
              )}
            </View>

            {/* Organization Filter - rightmost in RTL = first reading position */}
            {canViewAllOrgs && (
              <View style={{ width: isMobile ? '100%' : 'auto', zIndex: 200 }}>
                <OrganizationFilterDropdown
                  selectedOrgId={selectedOrgId}
                  onChange={setSelectedOrgId}
                />
              </View>
            )}
          </View>
          }
          style={{ marginBottom: tokens.spacing.absolute["6"] }}
        />

        {/* Initiatives Grid - minHeight to prevent CLS */}
        <View style={{ minHeight: 600 }}>
          {initiatives.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>{t('initiatives.noInitiatives')}</Text>
            </View>
          ) : (
            <>
              <View style={[styles.initiativesGrid, isSmallScreen && styles.initiativesGridSmall]}>
                {initiatives.map((initiative) => (
                  <View key={initiative.id} style={isSmallScreen ? styles.initiativeCardWrapperSmall : styles.initiativeCardWrapper}>
                    <InitiativeCard
                      initiative={initiative}
                      onPress={() => handleViewDetails(initiative.id)}
                      onEdit={() => handleEditInitiative(initiative)}
                      onDelete={() => handleDeletePress(initiative)}
                      isViewOnly={isViewOnly}
                      themedCardStyles={themedCardStyles}
                      iconColor={colors.iconDefault}
                      textColor={colors.textPrimary}
                      trackColor={colors.border}
                      dropdownBg={colors.card}
                      dropdownBorder={colors.cardBorder}
                      statBoxBg={themeColors.statBox}
                      t={t}
                      translate={translate}
                      translateOrg={translateOrg}
                    />
                  </View>
                ))}
              </View>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  nextLabel={t('pagination.next')}
                  previousLabel={t('pagination.previous')}
                />
              )}
            </>
          )}
        </View>

        <EvidenceTable
          relatedType="initiative_collection"
          filterRelatedId={canViewAllEvidence ? undefined : (ownDeptId ?? undefined)}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence && !!ownDeptId}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'initiative_collection',
            relatedId: ownDeptId as number,
            contextLabel: t('initiatives.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id,
            relatedType: ev.related_type,
            relatedId: ev.related_id,
            contextLabel: t('initiatives.title'),
          })}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setInitiativeToDelete(null); }}
        onConfirm={handleConfirmDelete}
        entityName={initiativeToDelete?.title || ''}
        fetchImpact={async () => {
          const response = await initiativesApi.deletionImpact(initiativeToDelete!.id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={deleting}
      />
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["5"],
  },
  contentSmall: {
    padding: tokens.spacing.absolute["3"],
  },
  // Breadcrumb
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbSeparator: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbCurrent: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Title Section Card - White card containing title, button and filters
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  // Row 2: Filter Section
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["5"],
    zIndex: 1000,
    overflow: 'visible',
  },
  filterRowSmall: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["2_5"],
    justifyContent: 'flex-end',
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    minHeight: 32,
    gap: tokens.spacing.absolute["2"],
  },
  filterDropdownText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Filter dropdown container
  filterDropdownContainer: {
    position: 'relative',
    zIndex: 9999,
    minWidth: 200,
  },
  filterOverlay: {
    position: 'fixed' as any,
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    zIndex: 9998,
  },
  filterDropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 50,
    zIndex: 9999,
    marginTop: tokens.spacing.absolute["1"],
    overflow: 'hidden',
  },
  filterDropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
    gap: tokens.spacing.absolute["2"],
  },
  filterDropdownOptionSelected: {
    backgroundColor: tokens.colors.raw.slate["100"],
  },
  filterDropdownOptionText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  filterCheckmark: {
    width: 20,
    height: 20,
    borderRadius: tokens.radii.absolute["radius-10"],
    backgroundColor: tokens.colors.raw.green["500"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterCheckmarkText: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontWeight: '600',
  },
  filterLoadingContainer: {
    padding: tokens.spacing.absolute["5"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Initiatives Grid
  initiativesGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["5"],
    overflow: 'visible',
    alignItems: 'stretch',
  },
  initiativesGridSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  // Card Wrappers for responsive width
  initiativeCardWrapper: {
    width: 'calc(50% - 10px)' as any,
    display: 'flex' as any,
  },
  initiativeCardWrapperSmall: {
    width: '100%',
    display: 'flex' as any,
  },
  // Initiative Card
  initiativeCard: {
    width: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  cardTopSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.absolute["6"],
    overflow: 'hidden',
  },
  gaugeContainer: {
    // Gauge on the left
  },
  cardTitleSection: {
    flex: 1,
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["3"],
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  // Purple badge for pending status - appears at top left
  pendingBadge: {
    backgroundColor: '#9467BD',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: 40,
  },
  pendingBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.white,
  },
  // Gold badge for organization unit name
  orgBadge: {
    backgroundColor: '#A6813F',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: 40,
  },
  orgBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.white,
  },
  cardDescription: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  // Stats Row - Gray boxes
  statsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  statBox: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    alignItems: 'flex-end',
    gap: tokens.spacing.absolute["1"],
    overflow: 'hidden',
  },
  statLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  statValue: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
    textAlign: 'center',
  },
  // Card Footer
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'visible',
    zIndex: 10,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    borderRadius: tokens.radii.absolute["radius-4"],
    gap: tokens.spacing.absolute["2"],
  },
  viewDetailsText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Empty State
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  // Pagination
  paginationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: tokens.spacing.absolute["8"],
    gap: tokens.spacing.absolute["2"],
  },
  paginationNavButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    minHeight: 36,
    borderRadius: tokens.radii.absolute["radius-8"],
    gap: tokens.spacing.absolute["2"],
  },
  paginationNavButtonDisabled: {
    opacity: 0.5,
  },
  paginationNavText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: tokens.colors.raw.slate["700"],
  },
  paginationPages: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  paginationPage: {
    width: 34,
    minHeight: 36,
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
  },
  paginationPageActive: {
    backgroundColor: COLORS.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  paginationPageText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '700',
    color: tokens.colors.raw.slate["700"],
  },
  paginationPageTextActive: {
    color: tokens.colors.semantic.general.foreground,
  },
  // Menu Container & Dropdown
  menuContainer: {
    position: 'relative',
    zIndex: 100,
  },
  menuOverlay: {
    position: 'fixed' as any,
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    zIndex: 99,
  },
  cardDropdownMenu: {
    position: 'absolute',
    bottom: '100%',
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px -4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 20,
    zIndex: 100,
    minWidth: 160,
    marginBottom: tokens.spacing.absolute["1"],
    overflow: 'hidden',
  },
  cardDropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["100"],
  },
  cardDropdownItemDelete: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  cardDropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  cardDropdownItemTextDelete: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
  },
  // Delete Confirmation Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 400,
    gap: tokens.spacing.absolute["4"],
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalCloseButton: {
    padding: tokens.spacing.absolute["1"],
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    flex: 1,
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 22,
  },
  modalInfoBox: {
    backgroundColor: tokens.colors.raw.amber["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["1_5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: '#FCD34D',
  },
  modalInfoTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: '#92400E',
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["1"],
  },
  modalInfoText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: '#92400E',
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  modalActions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
  },
  modalButton: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonDanger: {
    backgroundColor: tokens.colors.raw.red["500"],
  },
  modalButtonPrimary: {
    backgroundColor: tokens.colors.semantic.general["Main Color"],
  },
  modalButtonCancel: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
  },
  modalButtonTextDanger: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  modalButtonTextCancel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
});

const styles = getStyles(true);
